/**
 * Where a customer goes to allow local network access, in the browser they are actually using.
 *
 * The instructions used to name Firefox alone, because Firefox implemented the gate first. Chromium shipped
 * its own prompt in Chrome 142, so Chrome and Edge users now reach this screen too — and "open the padlock
 * and set Apps on Device to Allow" describes a control their browser does not have. Sending someone to look
 * for a setting that isn't there reads as the software being broken.
 *
 * A pure function of the user-agent string so it can be tested: there is no component testing here, and the
 * wording is the whole substance of the fix.
 */

export type BrowserFamily = 'chromium' | 'firefox' | 'safari' | 'unknown';

/**
 * Which browser family a user-agent belongs to.
 *
 * Edge is checked before Chrome and Chrome before Safari, because each of those user-agents contains the
 * name of the one after it — Edge's carries `Chrome`, and every Chromium UA carries `Safari`.
 */
export function browserFamily(ua: string): BrowserFamily {
  if (/Firefox\/|FxiOS/.test(ua)) return 'firefox';
  if (/Edg\/|EdgiOS/.test(ua)) return 'chromium';
  if (/Chrome\/|CriOS|Chromium/.test(ua)) return 'chromium';
  if (/Safari\//.test(ua)) return 'safari';
  return 'unknown';
}

export interface PermissionHelp {
  /** What the permission is called in this browser's own interface. */
  controlName: string;
  /** How to reach it, in the order the customer will do it. */
  steps: string[];
}

/**
 * How to grant the permission, and what it is called where they will see it.
 *
 * Deliberately describes the address-bar route rather than a settings URL: a `chrome://` or `about:` link
 * cannot be opened by a page, so offering one as a link would produce a control that does nothing.
 */
export function permissionHelp(ua: string, state: 'prompt' | 'denied'): PermissionHelp {
  const family = browserFamily(ua);
  if (family === 'firefox') {
    return {
      controlName: 'Apps on Device',
      steps: [
        'Click the padlock in the address bar.',
        `Set “Apps on Device” to Allow${state === 'denied' ? ' — it is currently blocked' : ''}.`,
        'Reload this page.',
      ],
    };
  }
  if (family === 'chromium') {
    return {
      controlName: 'Local network access',
      steps: [
        'Click the icon at the left of the address bar (a padlock, or “View site information”).',
        `Find “Local network access” and set it to Allow${state === 'denied' ? ' — it is currently blocked' : ''}.`,
        'Reload this page.',
      ],
    };
  }
  /*
   * Safari has no such permission today, and an unrecognised browser may have anything. Both land here only
   * because the browser reported a real state, so it does gate this — the honest answer is the generic route
   * plus the browsers known to work.
   */
  return {
    controlName: 'local network access',
    steps: [
      'Open this site’s permissions from the address bar.',
      'Allow access to devices on your local network.',
      'Reload this page. If there is no such setting, use Chrome, Edge or Firefox for capture.',
    ],
  };
}
